<template>
    <div class="checkout-course">

        <b-alert variant="danger" class="mb-4" :show="hasError">{{ error }}</b-alert>

        <section class="address bordered">
            <template v-if="isAddressEditing">
                <h2>Address</h2>
                <p>
                    Please enter your address.  We need this to be able to send you any materials that
                    may come with the course and help us with payment processing.
                </p>

                <div class="form-group">
                    <label for="address_name">Name</label>
                    <input type="text" class="form-control" id="address_name" v-model="address.name">
                </div>

                <div class="form-group">
                    <label for="address_line_1">Address Line 1</label>
                    <input type="text" class="form-control" id="address_line_1" v-model="address.line_1">
                </div>

                <div class="form-group">
                    <label for="address_line_2">Address Line 2 (optional)</label>
                    <input type="text" class="form-control" id="address_line_2" v-model="address.line_2">
                </div>

                <div class="form-group">
                    <label for="address_citytown">City/Town</label>
                    <input type="text" class="form-control" id="address_citytown" v-model="address.citytown">
                </div>

                <div class="form-group">
                    <label for="address_postcode">Postcode</label>
                    <input type="text" class="form-control" id="address_postcode" v-model="address.postcode">
                </div>

                <div class="form-group">
                    <b-button variant="primary" @click="saveAddress">Next...</b-button>
                </div>
            </template>
            <template v-if="!isAddressEditing">
                <h2>Address</h2>
                <p>
                    <strong>{{ address.name }}</strong>
                    {{ address.line_1 }},
                    {{ address.line_2 == '' ? '' : address.line_2 + ',' }}
                    {{ address.citytown }},
                    {{ address.postcode }}
                </p>
                <b-button variant="secondary" class="btn-sm" @click="isAddressEditing = true">Edit</b-button>
            </template>
        </section>

        <section class="payments bordered" v-show="hasAddress">
            <h2>Payment</h2>

            <template v-if="!isFreeToUser">
                <template v-if="isFreeDiscountCode">
                    <p>
                        Your discount code has made this course free.  Click to enrol.
                    </p>
                    <b-button variant="success" @click="fireEnrolment" :disabled="isBusy">Enrol</b-button>
                </template>
                <template v-else>
                    <p>
                        You are buying a <span v-html="purchaseDescriptorFormatted"></span> for
                        <strong>£{{ amountToPay }}</strong>
                        <template v-if="discountPercentage > 0">
                            (including a {{ discountPercentage }}% discount with code <em>{{ enteredDiscountCode }}</em>)
                        </template>
                    </p>
                    
                    <p>
                        Please choose a payment that best suits you.
                    </p>

                    <ul class="payment-type">
                        <li>
                            <b-form-radio v-model="type" name="type" value="pay_with_card" @change="initCard" :disabled="isBusy">Pay with card</b-form-radio>
                            <div class="payment-type-container" v-show="type == 'pay_with_card'">
                                <template v-if="isLoadingPaymentMethods">
                                    Loading...
                                </template>
                                <template v-else>
                                    <template v-if="paymentMethod">
                                        <div class="payment-btn-wrapper mb-3">                                    
                                            <span class="text-monospace ml-3">
                                                <span class="mr-3">{{ paymentMethodComputed[0] }}</span>
                                                **** **** **** {{ paymentMethodComputed[1] }}
                                            </span>
                                        </div>
                                        <p class="mb-3">
                                            <b-button variant="secondary" class="btn-sm" @click="showUpdatePaymentMethod" v-show="isShowUpdatePaymentMethodBtn" :disabled="isBusy">Update payment method</b-button>
                                        </p>

                                        <card-update-component 
                                            v-if="isShowUpdatePaymentMethod"
                                            :user="user"
                                            :is-inline="true"></card-update-component>

                                        <p> 
                                            <b-button variant="success" @click="handlePayment" :disabled="isBusy">Pay</b-button>
                                        </p>
                                    </template>
                                    <template v-else>
                                        <p>It looks like you don't have a card saved with us yet.</p>
                                        <b-button variant="primary" class="btn-sm" @click="showUpdatePaymentMethod" v-show="isShowUpdatePaymentMethodBtn">Update payment method</b-button>

                                        <card-update-component 
                                            v-if="isShowUpdatePaymentMethod"
                                            :user="user"
                                            :is-inline="true"></card-update-component>
                                    </template>                            
                                </template>
                            </div>
                        </li>
                        <!--
                        <li>
                            <b-form-radio v-model="type" name="type" value="pay_later" @change="initKlarna('pay_later')" :disabled="isBusy">Pay later with Klarna</b-form-radio>
                            <div class="payment-type-container" v-show="type == 'pay_later'">
                                <div id="klarna_pay_later_container"></div>
                                <template v-if="isKlarnaWidgetLoading">
                                    Loading...
                                </template>
                                <template v-else>
                                    <template v-if="isKlarnaPayLaterAvailable">
                                        <b-button variant="success" @click="handleKlarnaPayment('pay_later')" :disabled="isBusy">Pay</b-button>
                                    </template>
                                    <template v-else>
                                        <p>Sorry, Pay Later from Klarna isn't available for you right now.</p>
                                    </template>
                                </template>
                            </div>
                        </li>
                        -->
                        
                        <li>
                            <b-form-radio v-model="type" name="type" value="pay_over_time" @change="initKlarna('pay_over_time')" :disabled="isBusy">Pay in 3 with Klarna</b-form-radio>
                            <div class="payment-type-container" v-show="type == 'pay_over_time'">
                                <p>
                                    To pay in 3, you will need to <a href="https://app.klarna.com/" target="_blank">sign up for a Klarna account</a>.
                                </p>
                                <div id="klarna_pay_over_time_container"></div>
                                <template v-if="isKlarnaWidgetLoading">
                                    Loading...
                                </template>
                                <template v-else>
                                    <template v-if="isKlarnaPayOverTimeAvailable">
                                        <b-button variant="success" @click="handleKlarnaPayment('pay_over_time')" :disabled="isBusy">Pay</b-button>
                                    </template>
                                    <template v-else>
                                        <p>Sorry, Pay Over Time from Klarna isn't available for you right now.</p>
                                    </template>
                                </template>
                            </div>
                        </li>
                    </ul>
                </template>

                <hr>

                <div class="form-row">
                    <div class="col-md-4">
                        <div class="form-group">
                            <label for="discountCode">Have a discount code?  Enter it below.</label>
                            <div class="input-group">
                                <input type="text" class="form-control" id="discountCode" v-model="discountCode">
                                <div class="input-group-append">
                                    <b-button variant="primary" class="px-3" @click="handleDiscount">Go</b-button>
                                </div>
                            </div>
                            <b-alert variant="success" :show="discountSuccessMsg.length > 0">{{ discountSuccessMsg }}</b-alert>
                        </div>
                    </div>
                </div> 
            </template>
            <template v-else>
                <p>
                    This course is free.  Click below to complete the enrolment.
                </p>
                <b-button variant="success" @click="fireEnrolment" :disabled="isBusy">Enrol</b-button>
            </template>
        </section>

    </div>        
</template>

<script>
    export default {
        props: {
            course: {
                type: Object,
                required: true,
            },
            user: {
                type: Object,
                required: true,
            },
            slug: {
                type: String,
                required: true,
            },
            seat: {
                type: String,
                required: true,
            },
            isFreeToUser: {
                type: Boolean,
                required: true,
            }
        },

        data() {
            return {
                stripeAPIToken: document.querySelector('meta[name="stripe-key"]').getAttribute('content'),
                address: {
                    name: this.user.name,
                    line_1: '',
                    line_2: '',
                    citytown: '',
                    postcode: '',
                    country: 'UK'
                },
                isBusy: false,
                hasError: false,
                error: '',
                isAddressEditing: true,
                hasAddress: false,
                type: '',
                isLoadingPaymentMethods: false,
                paymentMethod: {},
                isShowUpdatePaymentMethod: false,
                isShowUpdatePaymentMethodBtn: true,
                isKlarnaWidgetLoading: true,
                isKlarnaPayLaterAvailable: false,
                isKlarnaPayOverTimeAvailable: false,
                klarnaSource: {},
                klarnaPaymentVirtuosoId: null,
                discountCode: '',
                enteredDiscountCode: '',
                discountSuccessMsg: '',
                discountPercentage: 0,
                discountAmount: 0,
                priceAfterDiscount: 0,
                appliedDiscountCodeId: null,
                isFreeDiscountCode: false,
            }
        },

        mounted() {
            // Load stripe
            this.includeScript('js.stripe.com/v3/', function(){
                this.configureGateways();
            }.bind(this) );

            // When payment methods have been updated refresh that section
            Bus.$on("updatedPaymentMethods", (d) => {
                this.isShowUpdatePaymentMethod = false;
                this.isShowUpdatePaymentMethodBtn = true;
                this.initCard();
            });

            if (typeof window.fbq === 'function') {
                fbq('track', 'InitiateCheckout');
            }
        },

        computed: {
            // a computed getter
            paymentMethodComputed: function () {                
                if(this.paymentMethod.card_type) {
                    return [
                        this.paymentMethod.card_type.charAt(0).toUpperCase() + this.paymentMethod.card_type.slice(1),
                        this.paymentMethod.last4 
                    ];
                } else {
                    return ['', ''];
                }
            },
            // User's name as per the address field
            userNameSplit: function() {
                if(!this.address.name) {
                    return ['', ''];
                }

                var firstName = this.address.name.split(' ').slice(0, -1).join(' ');
                var lastName = this.address.name.split(' ').slice(-1).join(' ');

                return [firstName, lastName];
            },
            // Amount to pay
            amountToPay: function() {
                let amt = 0;
                if(this.priceAfterDiscount > 0) {
                    amt = this.priceAfterDiscount;
                } else {
                    amt = this.course.userPrices[this.seat];
                }
                amt = parseFloat(Math.round(amt * 100) / 100).toFixed(2);

                return amt;
            },
            // Name of product being bought, sent to payment gateway (Klarna)
            purchaseDescriptor: function() {
                return (this.seat == 'active' ? 'Full Access' : this.seat) + ' seat on Course for '+ this.course.name;
            },
            // Same as above but with HTML formatting
            purchaseDescriptorFormatted: function() {
                return '<strong>'+ (this.seat == 'active' ? 'Full Access' : this.seat) + ' seat</strong> on <strong>'+ this.course.name +'</strong>';
            }
        },

        methods: {
            includeScript( URL, callback ){
                let documentTag = document, tag = 'script',
                    object = documentTag.createElement(tag),
                    scriptTag = documentTag.getElementsByTagName(tag)[0];
                object.src = '//' + URL;
                if (callback) { object.addEventListener('load', function (e) { callback(null, e); }, false); }
                scriptTag.parentNode.insertBefore(object, scriptTag);
            },

            // User has saved their address
            saveAddress() {
                if(this.address.name == '' || this.address.line_1 == '' || this.address.citytown == '' || this.address.postcode == '') {
                    this.error = 'Please ensure you fill out all required fields.';
                    this.hasError = true;
                } else {
                    this.error = '';
                    this.hasError = false;
                    this.isAddressEditing = false;
                    this.hasAddress = true;
                }
            },

            // Configures Stripe by setting up the elements and creating the card element.
            configureGateways() {
                this.stripe = Stripe( this.stripeAPIToken );
            },

            /** PAY BY CARD **/

            // User has opted to pay by card, load their payment methods
            initCard() {
                // Load payment methods
                this.isLoadingPaymentMethods = true;
                axios.get('/api/me/payment_method')
                    .then((response) => {
                        this.paymentMethod = response.data;
                        this.isLoadingPaymentMethods = false;
                    });
            },

            // Show payment method form
            showUpdatePaymentMethod() {
                this.isShowUpdatePaymentMethod = true;
                this.isShowUpdatePaymentMethodBtn = false;
            },

            // Handle Stripe Payment
            // Attempt payment and then enrol
            handlePayment() {
                this.isBusy = true;

                axios.post('/api/course/' + this.course.id + '/pay/' + this.seat, {
                    'address_name': this.address.name,
                    'address_line_1': this.address.line_1,
                    'address_line_2': this.address.line_2,
                    'address_citytown': this.address.citytown,
                    'address_postcode': this.address.postcode,
                    'discount_code_id': this.appliedDiscountCodeId,
                })
                    .then((response) => {
                        if(response.data.success) {
                            location.href = '/course/' + this.slug + '/' + this.course.id + '/enrol/' + this.seat + '/success?paid&method=stripe';
                        }

                        if(response.data.error) {
                            this.hasError = true;
                            this.error = response.data.error;
                            this.isBusy = false;
                        }

                        if(response.data.status) {
                            let payment_intent = response.data;                            

                            if (payment_intent) {
                                const { client_secret, status } = payment_intent;
                                if (status === 'requires_action') {
                                    this.stripe.confirmCardPayment(client_secret).then((result) => {
                                        if (result.error) {
                                            // Display error message in your UI.
                                            // The card was declined (i.e. insufficient funds, card has expired, etc)
                                            this.hasError = true;
                                            this.error = result.error.message;
                                            this.isBusy = false;
                                        } else {
                                            // Redirect after a timeout to give the webhook time to run
                                            setTimeout(() => {
                                                location.href = '/course/' + this.slug + '/' + this.course.id + '/enrol/' + this.seat + '/success?paid&method=stripe';
                                            }, 1500);
                                        }
                                    });
                                } else if(status === 'succeeded') {
                                    location.href = '/course/' + this.slug + '/' + this.course.id + '/enrol/' + this.seat + '/success?paid&method=stripe';
                                } else {
                                    this.hasError = true;
                                    this.error = 'An error was encountered.  Error code #2135';
                                }
                            }
                        }
                    })
                    .catch((error) => {
                        console.log(error);
                        this.hasError = true;
                        this.error = 'An unexpected error occured.';
                        this.isBusy = false;
                    });
            },

            /** KLARNA **/

            /*
                Klarna steps are as follows:
                  1.  Create a new 'klarna payment' locally.
                  2.  Initialise klarna.  Don't redo step 1 if we've already done it though.
                  3.  Create a 'source' within Stripe of Klarna.  Pass the internal klarna payment ID to this.
                  4.  Show Klarna widget.  Klarna takes over from here.
                  5.  Klarna should authorize our payment.  At this point we have a token.  Send that to our API to save against our internal Klarna payment object.  Create enrolment at same time as pending.
                  6.  Show user a success message.
                  7.  Stripe will then communicate with Klarna and make the source chargable.  This happens in a webhook.  Listen to source.chargeable in webhooks and then charge from there.
            */

             // Customer wants Klarna
            initKlarna(klarnaCategory) {
                this.isBusy = true;
                this.hasError = false;
                this.error = '';
                this.isKlarnaWidgetLoading = true;

                // Create a klarna payment reference locally
                if(!this.klarnaPaymentVirtuosoId) {
                    axios.post('/api/course/' + this.course.id + '/klarna/' + this.seat, {
                        'discount_code_id': this.appliedDiscountCodeId,
                    })
                    .then((response) => {
                        this.klarnaPaymentVirtuosoId = response.data.klarnaPaymentVirtuosoId;
                        this.createKlarnaSource(klarnaCategory);
                    })
                    .catch((error) => {
                        console.log(error);
                        this.hasError = true;
                        this.error = 'An unexpected error occured.  Please contact support or try a different payment method.';
                        this.isBusy = false;
                    });
                } else {
                    this.createKlarnaSource(klarnaCategory);
                }
            },

            // Create the Klarna source with Stripe
            createKlarnaSource(klarnaCategory) {
                let amount = this.amountToPay * 100;

                this.stripe.createSource({
                    type: 'klarna',
                    amount: amount,
                    currency: 'gbp',
                    klarna: {
                        product: 'payment',
                        purchase_country: 'UK',
                        first_name: this.user.firstName,
                        last_name: this.user.lastName,
                        shipping_first_name: this.user.firstName,
                        shipping_last_name: this.user.lastName,
                    },
                    owner: {
                        "email": this.user.email,
                        "name": this.user.firstName + ' ' + this.user.lastName,
                        'address': {
                            'line1': this.address.line_1,
                            'line2': this.address.line_2,
                            'city': this.address.citytown,
                            'postal_code': this.address.postcode,
                            'country': this.address.country,
                        }
                    },
                    source_order: {
                        items: [
                            {
                                type: 'sku',
                                description: this.seat + ' seat on Course for ' + this.course.name,
                                quantity: 1,
                                currency: 'gbp',
                                amount: amount,
                            }
                        ],
                        shipping: {
                            'address': {
                                'line1': this.address.line_1,
                                'line2': this.address.line_2,
                                'city': this.address.citytown,
                                'postal_code': this.address.postcode,
                                'country': this.address.country,
                            }
                        }
                    },
                    metadata: {
                        'klarnaPaymentVirtuosoId': this.klarnaPaymentVirtuosoId,
                        'virtuosoDescription': this.purchaseDescriptor, // Used when we make a charge to give it a pretty descriptor in Stripe CP
                    }
                }).then((result) => {
                    if(result.error) {
                        console.log(result);
                        this.hasError = true;
                        this.error = 'An unexpected error occured.';
                        this.isBusy = false;
                    } else {
                        this.klarnaSource = result.source;
                        this.klarnaWidgetInit(klarnaCategory);
                    }
                });
            },

            // Load the widget
            klarnaWidgetInit(klarnaCategory) {
                // Load Klarna
                this.includeScript('x.klarnacdn.net/kp/lib/v1/api.js', function() {
                    // Initialize the SDK
                    Klarna.Payments.init({
                        client_token: this.klarnaSource.klarna.client_token,
                    });

                    let category = klarnaCategory;

                    // Load the widget for each payment method category:
                    // - pay_later
                    // - pay_over_time
                    // - pay_now
                    //var available_categories = this.klarnaSource.klarna.payment_method_categories.split(',');
                    //console.log(available_categories);
                    //for (var category of available_categories) {
                        //console.log(category);
                        Klarna.Payments.load({
                            container: "#klarna_" + category + "_container",
                            payment_method_category: category,
                        }, (res) => {
                            if (res.show_form) {
                                // Available
                                console.log(category + ' is available');
                                if(category == 'pay_later') {
                                    this.isKlarnaPayLaterAvailable = true;
                                }
                                if(category == 'pay_over_time') {
                                    this.isKlarnaPayOverTimeAvailable = true;
                                }
                            } else {
                                // Not available                                
                                console.log(category + ' is not available');
                                if(category == 'pay_later') {
                                    this.isKlarnaPayLaterAvailable = false;
                                }
                                if(category == 'pay_over_time') {
                                    this.isKlarnaPayOverTimeAvailable = false;
                                }
                            }

                            this.isKlarnaWidgetLoading = false;
                            this.isBusy = false;
                        });
                    //}
                }.bind(this) );
            },

            // Process Klarna payment
            handleKlarnaPayment(type) {
                this.hasError = false;
                this.error = '';
                this.isBusy = true;

                // get the category the customer chose(using your own code)
                // Submit the payment for authorization with the selected category
                Klarna.Payments.authorize({
                    payment_method_category: type
                }, (res) => {
                    console.log(res);
                    if (res.approved) {
                        // Payment has been authorized

                        // Update the klarna payment id internally with the auth code and wait for webhook
                        axios.post('/api/course/' + this.course.id + '/klarna/' + this.seat + '/auth', {
                            'authorization_token': res.authorization_token,
                            'klarnaPaymentVirtuosoId': this.klarnaPaymentVirtuosoId,
                            'address_name': this.address.name,
                            'address_line_1': this.address.line_1,
                            'address_line_2': this.address.line_2,
                            'address_citytown': this.address.citytown,
                            'address_postcode': this.address.postcode,
                            'discount_code_id': this.appliedDiscountCodeId,
                        }).then((response) => {
                            console.log(response.data);

                            location.href = '/course/' + this.slug + '/' + this.course.id + '/enrol/' + this.seat + '/success?paid&method=klarna';
                        })
                        .catch((error) => {
                            console.log(error);
                            this.hasError = true;
                            this.error = 'An unexpected error occured.  Please contact support and quote reference ' + res.authorization_token + '.  Do not attempt to pay again.';
                            this.isBusy = true;
                        });
                    } else {
                        if (res.error) {
                            // Payment not authorized or an error has occurred
                            this.hasError = true;
                            this.error = res.error;
                            this.isBusy = false;
                        } else {
                            // handle other states
                            this.hasError = true;
                            this.error = 'Sorry, an unknown error has ocurred.  Please contact support or try another payment method.';
                            this.isBusy = false;
                        }
                    }
                })
            },

            // Actually enrol them.  Used when free.  There's a server-side check
            fireEnrolment() {
                this.isBusy = true;
                this.hasError = false;
                this.error = '';

                // Fire it
                axios.post('/api/course/' + this.course.id + '/enrol/' + this.seat, {                    
                    'address_name': this.address.name,
                    'address_line_1': this.address.line_1,
                    'address_line_2': this.address.line_2,
                    'address_citytown': this.address.citytown,
                    'address_postcode': this.address.postcode,
                    'discount_code_id': this.appliedDiscountCodeId,
                })
                    .then((response) => {
                        if(response.data.success) {
                            location.href = '/course/' + this.slug + '/' + this.course.id + '/enrol/' + this.seat + '/success?paid&method=free-course';
                        }

                        if(response.data.error) {
                            this.hasError = true;
                            this.error = response.data.error;
                            this.isBusy = false;
                        }
                    })
                    .catch((error) => {
                        console.log(error);
                        this.hasError = true;
                        this.error = 'An unexpected error occured.';
                        this.isBusy = false;
                    });
            },

            // Handle the discount - called when a user clicks/enters a discount code
            handleDiscount() {
                this.isBusy = true;
                axios.post('/api/discounts/course/' + this.course.id + '/check?seat=' + this.seat, {
                    'code': this.discountCode
                }).then((response) => {
                        if(response.data.success) {
                            this.isBusy = false;
                            this.hasError = false;
                            this.error = '';
                            this.discountPercentage = response.data.percentage;
                            this.discountSuccessMsg = 'A ' + this.discountPercentage + '% discount has been applied to your order.';
                            this.discountAmount = response.data.discountAmount;
                            this.priceAfterDiscount = response.data.priceAfterDiscount;
                            this.appliedDiscountCodeId = response.data.id;
                            this.enteredDiscountCode = this.discountCode;

                            if(this.discountPercentage == 100) {
                                this.isFreeDiscountCode = true;
                            } else {
                                this.isFreeDiscountCode = false;
                            }
                        }

                        if(response.data.error) {
                            this.hasError = true;
                            this.error = response.data.error;
                            this.isBusy = false;
                            this.discountPercentage = 0;
                            this.discountSuccessMsg = '';
                            this.discountAmount = 0;
                            this.priceAfterDiscount = 0;
                            this.appliedDiscountCodeId = null;
                        }
                    })
                    .catch((error) => {
                        console.log(error);
                        this.hasError = true;
                        this.error = 'An unexpected error occured.';
                        this.isBusy = false;
                    });
            }
        },
    }
</script>